import { Box, Checkbox, FormControlLabel, Grid, Radio, TextField } from "@mui/material";
import * as React from "react";
import { ConfirmationDialogResult, Permission } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as documentService from "../../../models/services/documentService";
import * as storeActions from "../../../models/store/storeActions";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IDocumentFolderEditItem } from "source/models/documentFolder/documentFolderItem";
import { IFolderNode } from "ui/Scripts/source/models/document/folderNode";
import { IUnitPermissionUserGroup } from "source/models/userGroup/unitPermissionUserGroup";
import { DialogTabs } from "source/components/framework/Dialog/DialogTabs";
import { FolderPermissions } from "./FolderPermissions";
import { AppIconFolder } from "source/components/framework/Icon/AppIconFolder";
import { DocumentFolderPath } from "../DocumentFolderPath/DocumentFolderPath";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

/* eslint-disable no-unused-vars */
export const enum DocumentFolderEditDialogMenuItem {
    General = "1",
    Permissions = "2",
    PermissionsOther = "3",
    ContentFiltering = "4"
}
/* eslint-enable no-unused-vars */

export interface IDocumentFolderEditDialogProp {
    sessionState: ISessionState;
    parentFolderPermission: Permission;
    id: string;
    companyId: string;
    isRoot: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onOk: (newSelectedId: string) => void;
    onCancel: (newSelectedId: string) => void;
    onRefreshList: () => Promise<void>;
}

export interface IDocumentFolderEditDialogState {
    activeMenuItem: DocumentFolderEditDialogMenuItem;
    
    canUseAllCompanyGroup: boolean;
    maxFolderNameLength: number;
    folderNode: IFolderNode;
    userGroups: IUnitPermissionUserGroup[];
    companyGroups: IUnitPermissionUserGroup[];
    fileFilters: IIdTitle[];
    userCompanyOwnsResouces: boolean;
    canEditProperties: boolean;
    hasFullPermissions: boolean;
    parentPath: string;

    id: string;
    rowId: string;

    code: string;
    path: string;
    comment: string;
    file: IFileAttachment;
    image: string;
    resetImage: boolean;
    fileFilterIds: string[];
    inheritedFileFilterIds: string[];
    showInheritedFileFilterIds: boolean;
       
    item: IDocumentFolderEditItem;
}

export class DocumentFolderEditDialog extends React.Component<IDocumentFolderEditDialogProp, IDocumentFolderEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IDocumentFolderEditDialogProp) {
        super(props);
        const permission = props.isRoot
            ? (props.sessionState.isSuperAdmin ? Permission.Full : Permission.None)
            : props.parentFolderPermission;
        const userCompanyOwnsResouces = props.sessionState.companyId === props.companyId;
        this.state = {
            activeMenuItem: DocumentFolderEditDialogMenuItem.General,
            canUseAllCompanyGroup: false,
            maxFolderNameLength: 248,
            folderNode: null,
            userGroups: [],
            companyGroups: [],
            fileFilters: [],
            item: null,
            userCompanyOwnsResouces,
            canEditProperties: permission > Permission.Read || userCompanyOwnsResouces,
            hasFullPermissions: props.sessionState.isSuperAdmin || userCompanyOwnsResouces,
            parentPath: "",
            id: "",
            rowId: "",
            code: "",
            path: "",
            comment: "",
            file: null,
            image: "",
            resetImage: false,
            fileFilterIds: [],
            inheritedFileFilterIds: [],
            showInheritedFileFilterIds: true,
            
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(this.state.hasFullPermissions
            ? () => documentService.getDocumentFolderEdit(props.companyId, id, props.isRoot)
            : () => documentService.getDocumentSharedFolderEdit(props.companyId, id, props.isRoot), false);
        if (!result) return;
        const item = result.item;
        result.folderNode.expanded = true;
        const state = {
            ...this.state,
            canUseAllCompanyGroup: result.canUseAllCompanyGroup,
            maxFolderNameLength: result.maxFolderNameLength,
            parentPath: result.path,
            folderNode: result.folderNode,
            userGroups: result.userGroups,
            companyGroups: result.companyGroups,
            fileFilters: result.fileFilters,
            item,
            id: item.id,
            rowId: item.rowId,
            code: item.code,
            path: item.path,
            comment: item.comment,
            fileFilterIds: item.fileFilterIds,
            inheritedFileFilterIds: item.inheritedFileFilterIds,
            showInheritedFileFilterIds: item.fileFilterIds.length < 1 && item.inheritedFileFilterIds.length > 0,
            file: null,
            image: item.image,
            resetImage: !item.image
        };
        this.setState(state);
        const saveData = await this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IDocumentFolderEditDialogProp, prevState: IDocumentFolderEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "path") {
            this.setState({ path: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleSetImage = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        if (!AppUtils.validateFileAttachment(attachment)) return;
        this.setState({ file: attachment, resetImage: false, image: attachment.thumbnail });
    };

    handleRemoveImage = () => {
        this.setState({ file: null, resetImage: true, image: "" });
    };

    handleToggleActiveMenuItem = (activeMenuItem: DocumentFolderEditDialogMenuItem) => {
        this.setState({ activeMenuItem });
    };

    handleChangeFolderNode = (folderNode: IFolderNode) => {
        this.setState({
            folderNode
        });
    };

    getSaveDataFromState = async (props: IDocumentFolderEditDialogProp, state: IDocumentFolderEditDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("isRoot", props.isRoot);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        if (props.sessionState.isSuperAdmin && props.isRoot) {
            result.append("path", state.path);
        }
        if (state.canEditProperties) {
            result.append("code", state.code);
            result.append("comment", state.comment);
            result.append("fileFilterIds", state.fileFilterIds);
            result.append("resetImage", state.resetImage);
            if (state.file) {
                result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file]));
            }
        }
        result.append("permissions", state.folderNode.getJsonFolderNodeSaves([]));
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            if (!state.path && this.props.sessionState.isSuperAdmin && this.props.isRoot) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.PathMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(this.state.hasFullPermissions
            ? () => documentService.saveDocumentFolderEdit(saveData.obj)
            : () => documentService.saveDocumentSharedFolderEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onOk(result.id);
            return;
        }
        this.props.onCancel(result.id);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (props.isRoot) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveFolderParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => documentService.removeFolder(props.companyId, state.id), true);
            if (!result) return;
            props.onCancel("");
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onCancel(state.id);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut((await this.getSaveDataFromState(props, this.state)).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="documentFolderDialog"
                maxWidth="md"
                title={Translations.Folder + ": " + state.item.code}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: DocumentFolderEditDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: DocumentFolderEditDialogMenuItem.Permissions, title: Translations.Permissions },
                            { id: DocumentFolderEditDialogMenuItem.PermissionsOther, title: Translations.PermissionsOtherCompanies, hidden: !state.userCompanyOwnsResouces || !state.companyGroups.length },
                            { id: DocumentFolderEditDialogMenuItem.ContentFiltering, title: Translations.ContentFiltering, hidden: !state.userCompanyOwnsResouces }
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <Box display="flex" flexDirection="column">
                        {state.activeMenuItem === DocumentFolderEditDialogMenuItem.General &&
                            <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <Box display="flex" alignItems="center" sx={{ paddingTop: ".5rem" }}>
                                            <div className="pictureContainer form-group">
                                                <AvatarImage
                                                    size={60}
                                                    id={state.id}
                                                    name={state.code}
                                                    icon={state.resetImage ? <AppIconFolder sx={{ "width": 24, "height": 24 }} /> : null}
                                                    image={state.resetImage ? null : state.image}
                                                    editable={state.canEditProperties}
                                                    tooltip={Translations.CkickToChangeThePicture}
                                                    removeTooltip={Translations.RemovePicture}
                                                    onChange={this.handleSetImage}
                                                    onClear={state.resetImage ? null : this.handleRemoveImage}
                                                    onError={AppUtils.showErrorMessage}
                                                />
                                            </div>
                                            <Box sx={{ marginTop: "-46px", paddingLeft: "20px", width: "100%" }}>
                                                <FormField title={Translations.FolderName} required>
                                                    <TextField name="code" title={Translations.FolderName} inputProps={{ maxLength: 248 }} value={state.code} required={true} disabled={!state.canEditProperties} onChange={this.handleChange} size="small" fullWidth />
                                                    <DocumentFolderPath path={state.parentPath} />
                                                </FormField>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </GridRow>
                                {props.sessionState.isSuperAdmin && props.isRoot &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.Path} fullWidth required margin="0">
                                                <TextField name="path" title={Translations.Description} inputProps={{ maxLength: 100 }} value={state.path} onChange={this.handleChange} size="small" fullWidth />
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Description} fullWidth margin={props.sessionState.isSuperAdmin && props.isRoot ? null : "0"}>
                                            <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} disabled={!state.canEditProperties} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {state.activeMenuItem === DocumentFolderEditDialogMenuItem.Permissions &&
                            <FolderPermissions
                                canUseAllCompanyGroup={true}
                                companyId={props.companyId}
                                folderId={state.id}
                                userGroupMode={true}
                                folderNode={state.folderNode}
                                groups={state.userGroups}
                                onChangeSaveFolderNode={this.handleChangeFolderNode}
                            />
                        }
                        {state.activeMenuItem === DocumentFolderEditDialogMenuItem.PermissionsOther &&
                            <FolderPermissions
                                canUseAllCompanyGroup={state.canUseAllCompanyGroup}
                                companyId={props.companyId}
                                folderId={state.id}
                                userGroupMode={false}
                                folderNode={state.folderNode}
                                groups={state.companyGroups}
                                onChangeSaveFolderNode={this.handleChangeFolderNode}
                            />
                        }
                        {state.activeMenuItem === DocumentFolderEditDialogMenuItem.ContentFiltering &&
                            <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <div>
                                            <FormControlLabel
                                                label={Translations.AllDocumentTypes + (state.inheritedFileFilterIds.length === 0 ? " " + Translations.InheritedContentFilter : "")}
                                                control={
                                                    <Radio
                                                        checked={state.fileFilterIds.length + state.inheritedFileFilterIds.length < 1}
                                                        onChange={(_e, checked: boolean) => {
                                                            if (!checked) return;
                                                            this.setState({ fileFilterIds: [] });
                                                        }}
                                                        disabled={state.inheritedFileFilterIds.length > 0}
                                                    />
                                                }
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                label={Translations.SelectedDocumentTypes}
                                                control={
                                                    <Radio
                                                        checked={state.fileFilterIds.length + state.inheritedFileFilterIds.length > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            if (!checked) return;
                                                            this.setState({ fileFilterIds: state.fileFilters.map(i => i.id) });
                                                        }}
                                                    />
                                                }
                                            />
                                        </div>
                                        <Box sx={{ paddingLeft: "1rem" }}>
                                            {state.fileFilters.map((fileFilter) =>
                                                <div key={fileFilter.id}>
                                                    <FormControlLabel
                                                        label={fileFilter.title + (state.inheritedFileFilterIds.indexOf(fileFilter.id) > -1 ? " " + Translations.InheritedContentFilter : "")}
                                                        control={
                                                            <Checkbox
                                                                checked={state.fileFilterIds.indexOf(fileFilter.id) > -1 || state.showInheritedFileFilterIds && state.inheritedFileFilterIds.indexOf(fileFilter.id) > -1}
                                                                onChange={(_e, checked: boolean) => {
                                                                    const fileFilterIds = state.showInheritedFileFilterIds
                                                                        ? state.inheritedFileFilterIds.filter(i => i !== fileFilter.id)
                                                                        : state.fileFilterIds.filter(i => i !== fileFilter.id);
                                                                    if (checked) {
                                                                        fileFilterIds.push(fileFilter.id);
                                                                    }
                                                                    this.setState({ fileFilterIds, showInheritedFileFilterIds: false });
                                                                }}
                                                                disabled={state.inheritedFileFilterIds.length > 0 && state.inheritedFileFilterIds.indexOf(fileFilter.id) < 0}
                                                            />
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </Box>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={false}
                            hasNext={false}
                            onPrevious={null}
                            onNext={null}
                            createUser={state.canEditProperties ? state.item.createUser : null}
                            modifyUser={state.canEditProperties ? state.item.modifyUser : null}
                        />
                        <DialogButtons
                            onCancel={this.handleCancel}
                            onRemove={!props.isRoot ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}